/**
 * Karsten-based land selection algorithm.
 *
 * Phase 0: Guaranteed slots (staples, utility, min basics, fetches)
 * Phase 1: Greedy deficit-filling by Karsten target
 * Phase 2: Basics fill remaining slots
 *
 * Implements F-029 rev 4 architecture §5.
 * Synchronous — runs on main thread in O(candidates × colors) time.
 */
import { computeKarstenSources, computeKarstenTargets, expandHybridPipCounts } from './karsten-sources.js';
// ─── Color <→ BasicType mapping ───────────────────────────────────────────────
const COLOR_TO_BASIC = {
    W: 'Plains',
    U: 'Island',
    B: 'Swamp',
    R: 'Mountain',
    G: 'Forest',
};
const BASIC_NAME = {
    W: 'Plains',
    U: 'Island',
    B: 'Swamp',
    R: 'Mountain',
    G: 'Forest',
};
// ─── Tier order (AD-065) ──────────────────────────────────────────────────────
export const KARSTEN_TIER_ORDER = [
    'triome', 'shock', 'marvel-dual', 'surveil', 'bond', 'verge', 'pain',
    'check', 'fast', 'slow', 'reveal', 'turbulent', 'battle', 'bounce', 'rainbow',
];
// ─── buildSimLandCard ─────────────────────────────────────────────────────────
/**
 * Convert a `LandCatalogEntry` and its `LandCycle` into a `SimLandCard`.
 * Maps category → simulation fields per architecture §6.1.
 */
export function buildSimLandCard(entry, cycle) {
    const category = cycle.category;
    const producedColors = new Set(entry.produces);
    const basicLandTypes = new Set((entry.basicLandTypes ?? []));
    const isFetch = category === 'fetch-on' || category === 'fetch-off';
    const isBasic = category === 'basic';
    const isVerge = category === 'verge';
    let alwaysUntapped = false;
    let alwaysTapped = false;
    let conditionalType;
    switch (category) {
        case 'shock':
        case 'bond':
        case 'pain':
        case 'pathway':
        case 'horizon':
        case 'og-dual':
        case 'rainbow':
        case 'basic':
        case 'verge':
            alwaysUntapped = true;
            break;
        case 'check':
            conditionalType = 'check';
            break;
        case 'fast':
            conditionalType = 'fast';
            break;
        case 'slow':
            conditionalType = 'slow';
            break;
        case 'reveal':
            conditionalType = 'reveal';
            break;
        case 'battle':
            conditionalType = 'battle';
            break;
        case 'surveil':
        case 'bounce':
        case 'triome':
        case 'tainted':
        case 'turbulent':
            alwaysTapped = true;
            break;
        case 'filter':
            alwaysUntapped = true;
            break;
        case 'fetch-on':
        case 'fetch-off':
            alwaysUntapped = true;
            break;
        default:
            alwaysUntapped = true;
            break;
    }
    let checksFor;
    let searchTypes;
    if (category === 'check' || category === 'reveal') {
        checksFor = new Set((entry.fetchTypes ?? []));
    }
    if (isFetch) {
        searchTypes = new Set((entry.fetchTypes ?? []));
    }
    let freeColor;
    let conditionalColor;
    let vergeCondition;
    if (isVerge) {
        const produces = entry.produces;
        freeColor = produces[0];
        conditionalColor = produces[1];
        if (conditionalColor && COLOR_TO_BASIC[conditionalColor]) {
            vergeCondition = new Set([COLOR_TO_BASIC[conditionalColor]]);
        }
    }
    return {
        name: entry.name,
        producedColors,
        basicLandTypes,
        isLand: true,
        alwaysUntapped,
        alwaysTapped,
        ...(conditionalType !== undefined ? { conditionalType } : {}),
        ...(checksFor !== undefined ? { checksFor } : {}),
        isFetch,
        ...(searchTypes !== undefined ? { searchTypes } : {}),
        isBasic,
        isVerge,
        ...(freeColor !== undefined ? { freeColor } : {}),
        ...(conditionalColor !== undefined ? { conditionalColor } : {}),
        ...(vergeCondition !== undefined ? { vergeCondition } : {}),
    };
}
// ─── computeFetchProfile ──────────────────────────────────────────────────────
/**
 * Compute which colors a fetch land can produce given the currently selected typed duals.
 */
export function computeFetchProfile(fetch, otherLands) {
    const reachableColors = new Set();
    if (!fetch.searchTypes || fetch.searchTypes.size === 0)
        return reachableColors;
    for (const land of otherLands) {
        if (land.isFetch)
            continue;
        if (land.basicLandTypes.size === 0)
            continue;
        let canFetch = false;
        for (const bt of land.basicLandTypes) {
            if (fetch.searchTypes.has(bt)) {
                canFetch = true;
                break;
            }
        }
        if (canFetch) {
            for (const color of land.producedColors) {
                reachableColors.add(color);
            }
        }
    }
    // Also include basics (fetch can always find basic lands of its search types)
    for (const bt of fetch.searchTypes) {
        const color = Object.entries(COLOR_TO_BASIC).find(([, t]) => t === bt)?.[0];
        if (color)
            reachableColors.add(color);
    }
    return reachableColors;
}
// ─── buildSimLandCardWithCategory ─────────────────────────────────────────────
/**
 * Build a SimLandCard with category annotation for tier matching.
 */
export function buildSimLandCardWithCategory(entry, cycle) {
    const card = buildSimLandCard(entry, cycle);
    return { ...card, _category: cycle.category };
}
// ─── makeBasicLand ────────────────────────────────────────────────────────────
export function makeBasicLand(color) {
    const bt = COLOR_TO_BASIC[color];
    return {
        name: BASIC_NAME[color],
        producedColors: new Set([color]),
        basicLandTypes: bt ? new Set([bt]) : new Set(),
        isLand: true,
        alwaysUntapped: true,
        alwaysTapped: false,
        isFetch: false,
        isBasic: true,
        isVerge: false,
    };
}
// ─── selectLandsKarsten ───────────────────────────────────────────────────────
/**
 * Karsten-based land selection. Three phases:
 *
 * Phase 0: Guaranteed slots in this order:
 *   1. Staple (rainbow catalog) lands
 *   2. Kept utility lands
 *   3. Minimum basics per color
 *   4. Fetch lands (skip for mono-color)
 *
 * Phase 1: Greedy deficit reduction by Karsten target.
 *   - Score each candidate by deficitReduction = sum(min(deficit[C], sourcesAdded[C]))
 *   - Tie-break by KARSTEN_TIER_ORDER, then alphabetical
 *   - Stop when all deficits ≤ 0, pool exhausted, or slots filled
 *
 * Phase 2: Basics fill remaining slots.
 *   - Pick color with max deficit; if all ≤ 0, pick color with fewest current basics.
 *
 * Synchronous. Returns immediately (sub-1ms for typical 36-slot decks).
 */
export function selectLandsKarsten(input) {
    const { candidates, colorIdentity, totalLandSlots, stapleNames, keptUtilityNames, minBasicsPerColor, categoryConstraints, spells, commanders, lockedLands, } = input;
    if (colorIdentity.length === 0) {
        return {
            selectedNames: [],
            landSources: new Map(),
            targets: { targets: new Map(), strictFloors: new Map() },
            pipCounts: new Map(),
        };
    }
    // ── Compute pip counts ──────────────────────────────────────────────────────
    const rawPips = new Map();
    for (const spell of spells) {
        for (const [key, count] of spell.colorRequirements.entries()) {
            rawPips.set(key, (rawPips.get(key) ?? 0) + count);
        }
    }
    for (const commander of commanders) {
        const manaCost = (() => {
            // Parse from requirements (already parsed by classifyCommanders)
            return '';
        })();
        void manaCost;
        for (const [key, count] of commander.colorRequirements.entries()) {
            rawPips.set(key, (rawPips.get(key) ?? 0) + count);
        }
    }
    // Ensure all identity colors are present in pipCounts (even if 0)
    const pipCounts = expandHybridPipCounts(rawPips);
    for (const color of colorIdentity) {
        if (!pipCounts.has(color)) {
            pipCounts.set(color, 0);
        }
    }
    // Remove non-identity colors from pipCounts
    for (const color of Array.from(pipCounts.keys())) {
        if (!colorIdentity.includes(color)) {
            pipCounts.delete(color);
        }
    }
    // If no pips, set equal pip counts so targets are at base floor
    const totalPips = Array.from(pipCounts.values()).reduce((s, v) => s + v, 0);
    if (totalPips === 0) {
        for (const color of colorIdentity) {
            pipCounts.set(color, 1);
        }
    }
    // Compute Karsten targets
    const karstenTargets = computeKarstenTargets(colorIdentity.length, pipCounts);
    const currentLands = [...lockedLands];
    const slotsRemaining = () => totalLandSlots - currentLands.length;
    const categoryPlacedCounts = new Map();
    for (const land of lockedLands) {
        const cat = land._category;
        categoryPlacedCounts.set(cat, (categoryPlacedCounts.get(cat) ?? 0) + 1);
    }
    const placedNames = new Set(lockedLands.map((l) => l.name));
    function canAddCategory(cat) {
        const constraint = categoryConstraints.get(cat);
        if (!constraint?.max)
            return true;
        return (categoryPlacedCounts.get(cat) ?? 0) < constraint.max;
    }
    function addLand(land) {
        currentLands.push(land);
        placedNames.add(land.name);
        const cat = land._category;
        categoryPlacedCounts.set(cat, (categoryPlacedCounts.get(cat) ?? 0) + 1);
    }
    // ── Phase 0 ────────────────────────────────────────────────────────────────
    // Phase 0.1: Staple (rainbow catalog) lands
    for (const name of stapleNames) {
        if (slotsRemaining() <= 0)
            break;
        if (placedNames.has(name))
            continue;
        const candidate = candidates.find((c) => c.name === name);
        if (!candidate)
            continue;
        if (!canAddCategory(candidate._category))
            continue;
        addLand(candidate);
    }
    // Phase 0.2: Kept utility lands (not in catalog — build minimal SimLandCard)
    for (const name of keptUtilityNames) {
        if (slotsRemaining() <= 0)
            break;
        if (placedNames.has(name))
            continue;
        // Check if it's already a catalog candidate
        const catalogCandidate = candidates.find((c) => c.name === name);
        if (catalogCandidate) {
            if (!canAddCategory(catalogCandidate._category))
                continue;
            addLand(catalogCandidate);
        }
        else {
            // Utility land — treat as rainbow for source counting
            const utilityLand = {
                name,
                producedColors: new Set(colorIdentity),
                basicLandTypes: new Set(),
                isLand: true,
                alwaysUntapped: true,
                alwaysTapped: false,
                isFetch: false,
                isBasic: false,
                isVerge: false,
                _category: 'rainbow',
            };
            if (!canAddCategory('rainbow'))
                continue;
            addLand(utilityLand);
        }
    }
    // Phase 0.3: Minimum basics per color
    for (const color of colorIdentity) {
        const minCount = minBasicsPerColor[color] ?? 0;
        if (minCount <= 0)
            continue;
        // Count existing basics of this color already placed
        const alreadyPlaced = currentLands.filter((l) => l.isBasic && l.producedColors.has(color)).length;
        const toPlace = Math.max(0, minCount - alreadyPlaced);
        for (let i = 0; i < toPlace; i++) {
            if (slotsRemaining() <= 0)
                break;
            const basic = makeBasicLand(color);
            const taggedBasic = { ...basic, _category: 'basic' };
            addLand(taggedBasic);
        }
    }
    // Phase 0.4: Fetch lands (skip for mono-color — AD-064)
    if (colorIdentity.length >= 2) {
        const fetchOnCandidates = candidates.filter((c) => c._category === 'fetch-on' && !placedNames.has(c.name));
        const fetchOffCandidates = candidates.filter((c) => c._category === 'fetch-off' && !placedNames.has(c.name));
        for (const fetch of [...fetchOnCandidates, ...fetchOffCandidates]) {
            if (slotsRemaining() <= 0)
                break;
            if (placedNames.has(fetch.name))
                continue;
            if (!canAddCategory(fetch._category))
                continue;
            addLand(fetch);
        }
    }
    // ── Phase 0.5: Category minimum constraints ────────────────────────────────
    // Force-fill categories that have a min constraint not yet met.
    // Runs BEFORE fetchable floor so user-specified mins take priority
    // (and also count toward the fetchable ratio since surveil/triome are fetchable).
    for (const [cat, constraint] of categoryConstraints) {
        if (!constraint.min || constraint.min <= 0)
            continue;
        if (cat === 'fetch-on' || cat === 'fetch-off' || cat === 'basic')
            continue;
        const alreadyPlaced = categoryPlacedCounts.get(cat) ?? 0;
        const toPlace = Math.max(0, constraint.min - alreadyPlaced);
        if (toPlace <= 0)
            continue;
        // Pick best candidates in this category by deficit reduction
        const currentSources = computeKarstenSources(currentLands, colorIdentity);
        const catCandidates = candidates
            .filter((c) => c._category === cat && !placedNames.has(c.name))
            .sort((a, b) => {
            const scoreA = colorIdentity.reduce((s, color) => {
                const d = (karstenTargets.targets.get(color) ?? 0) - (currentSources.get(color) ?? 0);
                const added = computeKarstenSources([a], colorIdentity).get(color) ?? 0;
                return s + (d > 0 ? Math.min(d, added) : 0);
            }, 0);
            const scoreB = colorIdentity.reduce((s, color) => {
                const d = (karstenTargets.targets.get(color) ?? 0) - (currentSources.get(color) ?? 0);
                const added = computeKarstenSources([b], colorIdentity).get(color) ?? 0;
                return s + (d > 0 ? Math.min(d, added) : 0);
            }, 0);
            return scoreB - scoreA;
        });
        let placed = 0;
        for (const candidate of catCandidates) {
            if (placed >= toPlace)
                break;
            if (slotsRemaining() <= 0)
                break;
            addLand(candidate);
            placed++;
        }
    }
    // ── Phase 0.6: Fetchable floor ────────────────────────────────────────────
    // Ensure at least 1.5 fetchable targets per fetch land placed.
    // Runs AFTER min constraints so user-specified surveil/triome mins count
    // toward the fetchable ratio.
    const FETCHABLE_RATIO = 1.5;
    const FETCHABLE_CATEGORIES = new Set(['shock', 'surveil', 'triome', 'battle', 'og-dual', 'turbulent']);
    const fetchCount = currentLands.filter((l) => l.isFetch).length;
    const requiredFetchable = Math.ceil(fetchCount * FETCHABLE_RATIO);
    if (fetchCount > 0 && slotsRemaining() > 0) {
        let currentFetchable = currentLands.filter((l) => !l.isFetch && !l.isBasic && l.basicLandTypes.size > 0).length;
        const fetchableCandidates = candidates
            .filter((c) => !placedNames.has(c.name) && FETCHABLE_CATEGORIES.has(c._category))
            .sort((a, b) => {
            const tierA = KARSTEN_TIER_ORDER.indexOf(a._category);
            const tierB = KARSTEN_TIER_ORDER.indexOf(b._category);
            return (tierA === -1 ? 999 : tierA) - (tierB === -1 ? 999 : tierB);
        });
        for (const candidate of fetchableCandidates) {
            if (currentFetchable >= requiredFetchable)
                break;
            if (slotsRemaining() <= 0)
                break;
            if (placedNames.has(candidate.name))
                continue;
            if (!canAddCategory(candidate._category))
                continue;
            addLand(candidate);
            currentFetchable++;
        }
    }
    // ── Phase 1 ────────────────────────────────────────────────────────────────
    // Greedy deficit reduction + pip-weighted fill until candidates are exhausted.
    // Build remaining non-fetch, non-basic candidates not yet placed
    const phase1Pool = candidates.filter((c) => !placedNames.has(c.name) &&
        c._category !== 'fetch-on' &&
        c._category !== 'fetch-off' &&
        c._category !== 'basic');
    // Build tier index for tie-breaking
    const tierIndex = new Map(KARSTEN_TIER_ORDER.map((cat, i) => [cat, i]));
    while (phase1Pool.length > 0 && slotsRemaining() > 0) {
        // Recompute deficits (used for scoring which dual to pick, not for stopping)
        const currentSources = computeKarstenSources(currentLands, colorIdentity);
        const deficit = new Map();
        for (const color of colorIdentity) {
            const target = karstenTargets.targets.get(color) ?? 0;
            const d = target - (currentSources.get(color) ?? 0);
            deficit.set(color, d);
        }
        // Phase 1 never stops early due to deficits being met — keep adding duals
        // until the candidate pool is exhausted or all slots are full.
        // Basics are only considered AFTER duals run out (Phase 2).
        // Score each candidate in the pool
        let bestCandidate = null;
        let bestScore = -1;
        let bestTier = Infinity;
        let bestName = '';
        for (let i = 0; i < phase1Pool.length; i++) {
            const candidate = phase1Pool[i];
            // Check category max constraint
            if (!canAddCategory(candidate._category))
                continue;
            // Compute incremental sources added by this candidate
            const incrementalSources = computeKarstenSources([candidate], colorIdentity);
            // Score: deficit reduction (primary) + pip-weighted coverage (secondary).
            // When deficits are met, the pip-weighted coverage ensures we keep picking
            // duals that match the deck's color demands — a green-producing dual scores
            // higher than a blue-producing one in a green-heavy deck.
            let deficitScore = 0;
            let pipScore = 0;
            const totalPips = Array.from(pipCounts.values()).reduce((s, v) => s + v, 0) || 1;
            for (const color of colorIdentity) {
                const d = deficit.get(color) ?? 0;
                const added = incrementalSources.get(color) ?? 0;
                if (d > 0) {
                    deficitScore += Math.min(d, added);
                }
                if (added > 0) {
                    // Weight by pip share so excess sources favor the deck's dominant colors
                    pipScore += (pipCounts.get(color) ?? 0) / totalPips;
                }
            }
            // deficitScore is integers (1, 2, ...), pipScore is 0–1 range,
            // so deficits always dominate when present
            const score = deficitScore + pipScore * 0.1;
            // Tier for tie-breaking (lower index = higher priority)
            const tier = tierIndex.get(candidate._category) ?? 999;
            if (score > bestScore ||
                (score === bestScore && tier < bestTier) ||
                (score === bestScore && tier === bestTier && candidate.name < bestName)) {
                bestScore = score;
                bestTier = tier;
                bestName = candidate.name;
                bestCandidate = candidate;
            }
        }
        if (bestCandidate === null)
            break;
        addLand(bestCandidate);
        const idx = phase1Pool.indexOf(bestCandidate);
        if (idx !== -1)
            phase1Pool.splice(idx, 1);
    }
    // ── Phase 2 ────────────────────────────────────────────────────────────────
    // Fill remaining slots with basics.
    while (slotsRemaining() > 0) {
        // Recompute deficits
        const currentSources = computeKarstenSources(currentLands, colorIdentity);
        // Count current basics per color
        const currentBasicCounts = new Map();
        for (const color of colorIdentity) {
            currentBasicCounts.set(color, 0);
        }
        for (const land of currentLands) {
            if (land.isBasic) {
                for (const color of land.producedColors) {
                    if (colorIdentity.includes(color)) {
                        currentBasicCounts.set(color, (currentBasicCounts.get(color) ?? 0) + 1);
                    }
                }
            }
        }
        // Find color with highest deficit
        let bestColor = null;
        let bestDeficit = -Infinity;
        let bestBasicCount = Infinity;
        for (const color of colorIdentity) {
            const target = karstenTargets.targets.get(color) ?? 0;
            const d = target - (currentSources.get(color) ?? 0);
            if (d > bestDeficit || (d === bestDeficit && (currentBasicCounts.get(color) ?? 0) < bestBasicCount)) {
                bestDeficit = d;
                bestBasicCount = currentBasicCounts.get(color) ?? 0;
                bestColor = color;
            }
        }
        // If no deficit > 0, pick color with fewest basics
        if (bestDeficit <= 0 || bestColor === null) {
            let fewestBasicsColor = null;
            let fewestBasics = Infinity;
            for (const color of colorIdentity) {
                const bc = currentBasicCounts.get(color) ?? 0;
                if (bc < fewestBasics || (bc === fewestBasics && fewestBasicsColor !== null && color < fewestBasicsColor)) {
                    fewestBasics = bc;
                    fewestBasicsColor = color;
                }
            }
            bestColor = fewestBasicsColor ?? colorIdentity[0] ?? null;
        }
        if (bestColor === null)
            break;
        const basic = makeBasicLand(bestColor);
        const taggedBasic = { ...basic, _category: 'basic' };
        addLand(taggedBasic);
    }
    // ── Output ──────────────────────────────────────────────────────────────────
    const finalSources = computeKarstenSources(currentLands, colorIdentity);
    return {
        selectedNames: currentLands.map((l) => l.name),
        landSources: finalSources,
        targets: karstenTargets,
        pipCounts,
    };
}
//# sourceMappingURL=sim-selection.js.map